// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// ui.ts — the static markup. Screens, panels and the results breakdown.
//
// The attribution backlink lives in the footer on every screen except a live round, because that is
// how anybody finds the rest of the catalogue. There is deliberately no repository link in the game
// itself, and no licence copy anywhere in here — licensing lives in the repo.

import { COST, State, breakEven, flowRate, tierOf } from './sim';
import { ITEM_NAME, KIND_BLURB, KIND_NAME, Kind, MODES, MODE_IDS, Quirk, modeOf } from './modes';
import { hottest } from './render';

export const FOOTER =
  '<footer class="site-footer">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a></footer>';

const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function menuHtml(mode: string): string {
  return `
    <div class="menu">
      <h1>Grist</h1>
      <p class="tag">Every lane carries 1.25 a second for free. Push it harder and the ground gives out — and ground does not come back.</p>
      <div class="modes" role="group" aria-label="Choose a mode">
        ${MODE_IDS.map(
          (id) => `<button class="mode-btn" data-mode="${id}" aria-pressed="${id === mode}">
            <b>${MODES[id].name}</b><span>${esc(MODES[id].blurb)}</span>
          </button>`,
        ).join('')}
      </div>
      <button class="primary" id="play" style="width:100%">Start a shift</button>
      <div class="menu-row">
        <button id="howto">How to play</button>
        <button id="about">About</button>
        <button id="mute" aria-pressed="false">Sound on</button>
      </div>
    </div>`;
}

export function gameHtml(mode: string): string {
  const m = modeOf(mode);
  const tools = [Kind.Belt, Kind.Wye, Kind.Digger, Kind.Smelter, Kind.Mill, Kind.Jig];
  return `
    <div class="game">
      <div class="hud">
        <button id="pause" aria-label="Pause">II</button>
        <span class="score"><span id="shipped">0</span> shipped</span>
        <span class="clock"><i id="clockbar" style="width:0%"></i></span>
        <span class="scrip"><span id="scrip">${m.startScrip}</span> scrip</span>
      </div>
      <div class="strip" id="strip"></div>
      <div class="coach" id="coach" hidden>
        <span class="step" id="coachStep">1/7</span>
        <span id="coachCopy"></span>
        <button id="coachSkip">Skip</button>
      </div>
      <div class="board-wrap"><div id="board"></div></div>
      <div class="context">
        <button id="erase" aria-pressed="false">Erase</button>
        <button id="strain" aria-pressed="false">Strain</button>
        <button id="help">Rules</button>
      </div>
      <div class="dock">
        ${tools
          .map(
            (k) => `<button class="tool" data-kind="${k}" aria-pressed="false">
              <b>${KIND_NAME[k]}</b><span>${COST[k]}</span>
            </button>`,
          )
          .join('')}
      </div>
      <aside class="rail" id="rail"></aside>
    </div>`;
}

export function howToPlayHtml(mode: string): string {
  const m = modeOf(mode);
  return `<div class="sheet-head"><h2>How to play</h2><button class="close" aria-label="Close">✕</button></div>
    <p>Dig shale and loam, carry them along belts, and ship <b>Armatures</b> from the wharf. An armature is
    1 Billet + 1 Binder, which is 3 shale and 2 loam — so one armature is five things dragged across the yard.</p>
    <p><b>The one rule that matters:</b> a ground cell carries about <b>1.25 items a second</b> for nothing.
    Push more through it and the ground ruts: rutted ground moves items up to seven times slower, and ground
    held in the red long enough is <b>scoured</b> permanently. You cannot upgrade a belt. The only fix is
    another lane somewhere else.</p>
    <p>Rut colour is how much traffic that ground has carried. A <b>backed-up</b> lane is a different problem
    and shows a bright bar along its length. Machines only draw a mark when something is wrong.</p>
    <p><b>${m.name}:</b> ${esc(m.rule)}</p>
    <div class="rows">${[Kind.Belt, Kind.Wye, Kind.Digger, Kind.Smelter, Kind.Mill, Kind.Jig]
      .map((k) => `<div class="row"><b>${KIND_NAME[k]}</b> ${esc(KIND_BLURB[k])}<span>${COST[k]}</span></div>`)
      .join('')}</div>`;
}

export function aboutHtml(): string {
  return `<div class="sheet-head"><h2>About</h2><button class="close" aria-label="Close">✕</button></div>
    <p>Grist is a factory game about the one resource a factory game usually ignores: the ground itself.
    Throughput is bought with routing area and rest, never with a better belt.</p>
    <p>It runs entirely in this tab. There are no accounts, no cookies, no trackers and no networking of any
    kind — it works with the aeroplane mode on. Anonymous, cookie-less page counts come from Cloudflare Web
    Analytics, which is the only request this page makes.</p>
    <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.</p>`;
}

export function quirkHtml(qs: Quirk[]): string {
  return qs.map((q) => `<span class="chip">${esc(q.name)}</span>`).join('');
}

/**
 * The results screen. Principle #9: this is the moment a player compares themselves, so it shows
 * what the factory actually DID — every machine's share of running, starved and blocked time, and
 * the ground it spent — not just a number.
 */
export function summaryHtml(s: State, best: number): string {
  const m = modeOf(s.mode);
  const machines: number[] = [];
  for (let c = 0; c < s.kind.length; c++) {
    if (s.kind[c] === Kind.Smelter || s.kind[c] === Kind.Mill || s.kind[c] === Kind.Jig) machines.push(c);
  }
  const rows = machines
    .map((c) => {
      const run = s.censusRun[c];
      const st = s.censusStarved[c];
      const bl = s.censusBlocked[c];
      const tot = Math.max(1, run + st + bl);
      const pct = (v: number): string => `${Math.round((v / tot) * 100)}%`;
      return `<div class="row"><b>${KIND_NAME[s.kind[c]]}</b> running ${pct(run)} · starved ${pct(st)} · blocked ${pct(bl)}<span></span></div>`;
    })
    .join('');

  const hot = hottest(s, 3)
    .map(
      (c) =>
        `<div class="row"><b>Lane cell</b> rut ${s.rut[c]} (tier ${tierOf(s.rut[c])})${
          s.scour[c] ? `, scoured ${s.scour[c]}` : ''
        }<span>${flowRate(s, c).toFixed(2)}/s of ${breakEven(s.scour[c]).toFixed(2)}</span></div>`,
    )
    .join('');

  const spent = s.crossings > 0 ? Math.round((s.crossingsRutted / s.crossings) * 100) : 0;

  return `<div class="sheet-head"><h2>Whistle</h2><button class="close" aria-label="Close">✕</button></div>
    <p><b>${s.shipped}</b> armature${s.shipped === 1 ? '' : 's'} shipped in ${m.name}. Best ${Math.max(best, s.shipped)}.</p>
    <p><b>${spent}%</b> of everything you moved crossed ground you had already worn out.</p>
    <h3 style="font-size:14px;margin:14px 0 4px">What each machine did with its six minutes</h3>
    <div class="rows">${rows || '<div class="row">No machines were built.</div>'}</div>
    <h3 style="font-size:14px;margin:14px 0 4px">The ground you spent</h3>
    <div class="rows">${hot}</div>
    <div class="menu-row" style="margin-top:14px">
      <button class="primary" id="again">Another shift</button>
      <button id="tomenu">Menu</button>
      <button class="results-feedback" id="fb">Feedback</button>
    </div>`;
}

export const ITEM_LIST = ITEM_NAME;
